import React, { useState, useEffect, useMemo, useCallback } from "react";
import DataTable from "react-data-table-component";
import { Tooltip, OverlayTrigger, Button } from "react-bootstrap";
import DateTime from "../../../../components/Formatters/DateTime";
import TextSpinner from "../../../../components/UI/Spinner/TextSpinner";
import useDetalhamento from "../hooks/useDetalhamento";
import FormDemonstrativos from "../FormDemonstrativos";
import useMontarLogica from "../hooks/useMontarLogica";
import useBuscaComposicao from "../hooks/useBuscaComposicao";
import useExcluirDemonstrativo from "../hooks/useExcluirDemonstrativo";
import constants from "../../../Constants";

const getColumns = (mostrarColuna, sentidoOperacao, mostrarVlBaseCalculo) =>
  [
    { name: <div>Nº Doc. Fiscal</div>, selector: (row) => row.nuItem, maxWidth: "5%" },
    {
      name: "Chave",
      cell: (row) => row.txChave,
      style: {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: "20%",
        display: "block",
      },
    },
    {
      name: <div>Data Emissão</div>,

      cell: (row) => <DateTime value={row.dtEmissao} stringFormat="dd/MM/yyyy" />,
      center: true,
      minWidth: "5%",
    },
    {
      name: <div>Data Vencimento</div>,
      cell: (row) => <DateTime value={row.dtVencimento} stringFormat="dd/MM/yyyy" />,
      center: true,
      minWidth: "5%",
    },
    {
      name: sentidoOperacao === "S" ? <div>CNPJ/CPF Destinatário</div> : <div>CNPJ/CPF Emitente </div>,
      selector: (row) => row.nuCnpjRemet,
      minWidth: "10%",
    },
    {
      name: sentidoOperacao === "S" ? <div>UF Destino</div> : <div>UF Origem</div>,
      selector: (row) => row.siglaUf,
      center: true,
      minWidth: "5%",
    },
    {
      name: <div>Valor Documento (R$)</div>,
      cell: (row) =>
        row.valorTotalNota
          ? row.valorTotalNota.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : (0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      minWidth: "8%",
    },
    mostrarColuna && {
      name: <div>Agregado (%)</div>,
      cell: (row) =>
        row.pcAgregadoAuditor
          ? row.pcAgregadoAuditor.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : (0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      minWidth: "5%",
    },
    mostrarVlBaseCalculo && {
      name: <div>Base de Cálculo (R$)</div>,
      cell: (row) =>
        row.vlBaseCalculo
          ? row.vlBaseCalculo.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : (0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      minWidth: "8%",
    },
    mostrarColuna && {
      name: <div>Alíquota (%)</div>,
      cell: (row) =>
        row.pcIcmsAuditor
          ? row.pcIcmsAuditor.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : (0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      minWidth: "5%",
    },
    mostrarColuna && {
      name: <div>Crédito (R$)</div>,
      cell: (row) =>
        row.vlCredito
          ? row.vlCredito.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : (0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      minWidth: "8%",
    },
    {
      name: <div>ICMS a Recolher (R$)</div>,
      cell: (row) =>
        row.vlIcms
          ? row.vlIcms.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : (0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      minWidth: "8%",
    },
    {
      name: <div>Multa (R$)</div>,
      cell: (row) =>
        row.vlMultaOcorrenciaAtual
          ? row.vlMultaOcorrenciaAtual.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : (0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      minWidth: "8%",
    },
  ].filter(Boolean);

const conditionalRowStyles = [
  {
    when: (row) => row.nuItem === "Total",
    style: {
      backgroundColor: "#f0f0f0",
      cursor: "not-allowed",
      fontWeight: "bold", // Deixa a fonte em negrito
      color: "#333", // (opcional) ajusta a cor da fonte pra ficar mais visível
    },
  },
];

const ListagemM = ({ history, sqItemOcorrencia, sqAcaoFiscal }) => {
  const [reload, setReload] = useState(false);
  const [clearSelectedRows, setClearSelectedRows] = useState(false);
  const [itensSelecionados, setItensSelecionados] = useState([]);
  const { dataWithTotal, totalRows, isLoading, handlePageChange } = useDetalhamento({
    sqAcaoFiscal,
    sqItemOcorrencia,
    reload, // Passando para o hook para atualizar os dados
  });
  const [mostrarVlBaseCalculo, setMostrarVlBaseCalculo] = useState(true);
  const [mostrarColuna, setMostrarColuna] = useState(true);

  const { buscaComposicao, composicao, sentidoOperacao } = useBuscaComposicao({ sqAcaoFiscal, sqItemOcorrencia });

  useEffect(() => {
    if (sqAcaoFiscal !== null) {
      buscaComposicao();
    }
  }, [sqAcaoFiscal, sqItemOcorrencia, buscaComposicao]);

  useEffect(() => {
    if (composicao === "SNNN") {
      setMostrarColuna(false);
      setMostrarVlBaseCalculo(false);
    } else if (composicao !== "NNSS") {
      setMostrarColuna(false);
      setMostrarVlBaseCalculo(true);
    } else {
      setMostrarColuna(true);
      setMostrarVlBaseCalculo(true);
    }
  }, [composicao, sentidoOperacao, setMostrarColuna]);

  const [dados, setDados] = useState({});

  const { montarLogica, mostrarLista, setMostrarLista } = useMontarLogica();

  useEffect(() => {
    if (mostrarLista) {
      montarLogica(1);
    }
  }, [montarLogica, mostrarLista]);

  const voltarListagem = () => {
    setMostrarLista(true);
    setReload((prev) => !prev);
  };

  const { ModalExcluirDemonstrativo, setShowExcluirConfirm } = useExcluirDemonstrativo({
    setReload,
    setItensSelecionados,
  });

  const columns = useMemo(
    () => getColumns(mostrarColuna, sentidoOperacao, mostrarVlBaseCalculo),
    [mostrarColuna, sentidoOperacao, mostrarVlBaseCalculo],
  );

  const preparaDelete = useCallback(() => {
    if (itensSelecionados.length > 0) {
      setShowExcluirConfirm({
        show: true,
        body: `Confirma exclusão do(s) item(ns) no demonstrativo?`,
        context: { sqAcaoFiscal, sqItemOcorrencia, listaItens: itensSelecionados },
      });

      // Resetando a seleção e forçando novo fetch dos dados
      setClearSelectedRows(!clearSelectedRows);
      setItensSelecionados([]);

      // Força o reload completo para pegar os dados atualizados
      setReload((prev) => !prev);
    } else {
      document.getElementById("alerta").innerHTML = "Selecione pelo menos 1 item para excluir";
    }
  }, [itensSelecionados, sqAcaoFiscal, sqItemOcorrencia, setShowExcluirConfirm, clearSelectedRows]);

  return mostrarLista ? (
    <>
      {ModalExcluirDemonstrativo}

      <DataTable
        noHeader
        keyField={(row, index) => `${row.sqItemOcorrencia}-${index}`}
        columns={columns}
        data={dataWithTotal}
        progressPending={isLoading}
        noDataComponent="Nenhum registro para exibir"
        progressComponent={<TextSpinner />}
        pointerOnHover
        onRowClicked={(row) => {
          if (row.nuItem !== "Total") {
            setDados(row);
            montarLogica(4);
          }
        }}
        conditionalRowStyles={conditionalRowStyles}
        pagination
        paginationTotalRows={totalRows}
        onChangePage={handlePageChange}
        paginationComponentOptions={constants.paginationComponentOptions}
        paginationRowsPerPageOptions={[10, 20, 50, 100, 1000]}
        paginationPerPage={10}
        selectableRows
        selectableRowDisabled={(row) => row.nuItem === "Total"}
        selectableRowsVisibleOnly={(row) => !(row.nuItem === "Total")}
        onSelectedRowsChange={(state) => {
          setItensSelecionados(state.selectedRows);
        }}
        clearSelectedRows={clearSelectedRows}
        // style={{
        //   whiteSpace: "nomal",
        //   wordBreak: "break-word", // Força quebra de palavras longas
        //   overflow: "visible", // Remove overflow
        //   textOverflow: "unset", // Remove reticências
        //   lineHeight: "1.5", // Ajusta a altura da linha
        // }}
      />

      <span className="alerta" hidden={itensSelecionados.length > 0} id="alerta">
        &nbsp;
      </span>

      <br />
      <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-chave">Excluir itens do demonstrativo</Tooltip>}>
        <Button
          className="btn btn-primary btn-md mt-1" // btn btn-primary btn-md mr-2 btn btn-primary
          style={{ marginTop: "3px" }} // CSS inline direto
          disabled={itensSelecionados.length === 0}
          onClick={() => preparaDelete()}
        >
          Excluir
        </Button>
      </OverlayTrigger>
    </>
  ) : (
    <FormDemonstrativos
      tipoDetalhamento={dados.tipoDetalhamento}
      sqItemOcorrencia={dados.sqItemOcorrencia}
      sqAcaoFiscal={dados.sqAcaoFiscal}
      dadosOcorrencia={dados}
      history={history}
      voltarListagem={() => voltarListagem()}
    />
  );
};

export default ListagemM;
